import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSupabaseTypedClient } from 'hooks/reactQuery';
import { useToastWrapper } from 'hooks/useToastWrapper';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import { EdgeFunctionName, UploadAssetResponse } from 'types/edgeFunctions';
import { TanstackQueryName } from 'types/frontend';

interface UploadAssetVariables {
  file: File;
  entityType: string;
  entityId: string;
}

export const useUploadAssetMutation = () => {
  const supabase = useSupabaseTypedClient();
  const queryClient = useQueryClient();
  const { id: userId } = useCurrentUser();
  const { toastSuccess, toastError } = useToastWrapper();

  return useMutation<UploadAssetResponse, Error, UploadAssetVariables>({
    mutationFn: async ({ file, entityType, entityId }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entityType', entityType);
      formData.append('entityId', entityId);

      const { data, error } = await supabase.functions.invoke<UploadAssetResponse>(
        EdgeFunctionName.UploadAsset,
        { body: formData }
      );

      if (error) throw error;
      if (!data) throw new Error('No data returned');

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetUserAssets, userId] });
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetCurrentUser, userId] });
      toastSuccess('File uploaded');
    },
    onError: (error) => {
      toastError(error.message || "Can't upload file");
    },
  });
};
